/**
 * RefereesSection - Admin referees management
 *
 * Simple list of referees as pills, click to see stats in bottom sheet
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Users,
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Gift,
  Loader2,
} from 'lucide-react';
import {
  MenuButton,
  TogglePill,
  BottomSheet,
  InfoCardDetail,
} from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { DetailBox } from '@/components/shared';
import type { RefereeListItem } from '@club-bot/shared/contracts';

// =============================================================================
// TYPES
// =============================================================================

export interface RefereesSectionProps {
  referees: RefereeListItem[];
  isLoading?: boolean;
  onRefresh?: () => void;
  onNavigate?: (page: string) => void;
}

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'referees', label: 'Referees', icon: Users },
];

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(amount: number): string {
  return `${amount.toFixed(2)} AZN`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function buildRefereeBoxes(referee: RefereeListItem): DetailBox[] {
  return [
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { label: 'ID', value: referee.identifier },
        { label: 'Referral Code', value: referee.referralCode },
        { label: 'Name', value: referee.name },
        { label: 'Commission', value: `${referee.commissionPercent}%` },
      ],
      visible: true,
    },
    {
      id: 'earnings',
      title: 'Earnings',
      fields: [
        { label: 'Total Earned', value: referee.totalEarned, format: 'currency' },
        { label: 'Total Lost', value: referee.totalLost, format: 'currency' },
        { label: 'Net', value: referee.net, format: 'currency' },
      ],
      visible: true,
    },
    {
      id: 'withdrawals',
      title: 'Withdrawals & Conversions',
      fields: [
        { label: 'Total Withdrawn', value: referee.totalWithdrawn, format: 'currency' },
        { label: 'Total Converted', value: referee.totalConverted, format: 'currency' },
        { label: 'Balance', value: referee.balance, format: 'currency' },
      ],
      visible: true,
    },
    {
      id: 'dates',
      title: 'Dates',
      fields: [
        { label: 'Created', value: formatDate(referee.createdAt) },
        { label: 'Updated', value: formatDate(referee.updatedAt) },
      ],
      visible: true,
    },
  ];
}

// =============================================================================
// COMPONENT
// =============================================================================

export function RefereesSection({
  referees,
  isLoading = false,
  onRefresh,
  onNavigate,
}: RefereesSectionProps) {
  const [selectedReferee, setSelectedReferee] = useState<RefereeListItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Load on mount
  useEffect(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleRefereeClick = useCallback((referee: RefereeListItem) => {
    setSelectedReferee(referee);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedReferee(null);
  }, []);

  return (
    <>
      <div className="space-y-6 pb-6">
        {/* Header with menu */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800">Referees</h2>
          <MenuButton
            options={adminMenuOptions}
            value="referees"
            onSelect={(page) => onNavigate?.(page)}
            sheetTitle="Navigate"
          />
        </div>

        {/* Loading state */}
        {isLoading && referees.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && referees.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400 text-sm">No referees found</p>
          </div>
        )}

        {/* Referees pills */}
        {referees.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
              All Referees ({referees.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {referees.map((referee) => (
                <TogglePill
                  key={referee.identifier}
                  label={referee.name}
                  isSelected={selectedReferee?.identifier === referee.identifier}
                  onClick={() => handleRefereeClick(referee)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Quick stats summary */}
        {referees.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
              Summary
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs text-green-600 font-medium">Total Earned</p>
                <p className="text-lg font-bold text-green-700">
                  {formatCurrency(referees.reduce((sum, r) => sum + r.totalEarned, 0))}
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-blue-600 font-medium">Total Withdrawn</p>
                <p className="text-lg font-bold text-blue-700">
                  {formatCurrency(referees.reduce((sum, r) => sum + r.totalWithdrawn, 0))}
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-xs text-purple-600 font-medium">Total Converted</p>
                <p className="text-lg font-bold text-purple-700">
                  {formatCurrency(referees.reduce((sum, r) => sum + r.totalConverted, 0))}
                </p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-xs text-amber-600 font-medium">Total Balance</p>
                <p className="text-lg font-bold text-amber-700">
                  {formatCurrency(referees.reduce((sum, r) => sum + r.balance, 0))}
                </p>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Detail Bottom Sheet */}
      <BottomSheet
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        height="full"
      >
        {selectedReferee && (
          <InfoCardDetail
            title={selectedReferee.name}
            subtitle={`Code: ${selectedReferee.referralCode}`}
            status="active"
            boxes={buildRefereeBoxes(selectedReferee)}
            onClose={handleCloseDetail}
          />
        )}
      </BottomSheet>
    </>
  );
}
